import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { Grid } from '#app/components/layout/grid.tsx'
import { Code } from '#app/components/typography/code.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { P } from '#app/components/typography/p.tsx'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Parts, Usage } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Grid',
}

const component: componentProps = {
	name: 'Grid',
	shortName: 'Grid',
	fileName: 'grid',
	category: 'layout',
	description: ['Component for creating bidimensional grid layouts.'],
	features: ['Inherits the fluid space scale on the gapX and gapY props.', 'Removes inaccessible CSS features.'],
	parts: [
		{
			name: 'Grid',
			description: 'Component for creating bidimensional grid layouts.',
			props: [
				{ name: 'inline', type: 'boolean', default: 'false', description: 'Whether the grid is inline or not.' },
				{ name: 'cols', type: '"number"', default: '1', description: 'The number of columns.' },
				{ name: 'rows', type: '"number"', default: '1', description: 'The number of rows.' },
				{ name: 'justify', type: "'start' | 'center' | 'end' | 'between'", default: "'start'", description: 'The horizontal alignment of the grid.' },
				{ name: 'align', type: "'start' | 'center' | 'end' | 'stretch'", default: "'start'", description: 'The vertical alignment of the grid.' },
				{ name: 'gapX', type: '0 to 20', default: '0', description: 'The horizontal gap between the grid items.' },
				{ name: 'gapY', type: '0 to 20', default: '0', description: 'The vertical gap between the grid items.' },
			],
		},
	],
	usage: `<Grid cols="12" rows="3" gapX="6" gapY="6" className="bg-muted-200 mt-4 h-96 rounded-md p-4">
	<div className="bg-muted-600 col-span-12 flex h-full w-full items-center justify-center rounded-sm text-white">Header</div>
	<div className="bg-muted-600 col-span-3 flex h-full w-full items-center justify-center rounded-sm text-white">Sidebar</div>
	<div className="bg-muted-600 col-span-9 flex h-full w-full items-center justify-center rounded-sm text-white">Content</div>
	<div className="bg-muted-600 col-span-12 flex h-full w-full items-center justify-center rounded-sm text-white">Footer</div>
</Grid>`,
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const source = await readSource({ category: component.category, fileName: component.fileName })
	return json({ source })
}

const HeadingRoute = () => {
	const { source } = useLoaderData<typeof loader>()

	const { name, description, shortName, fileName, features, usage, parts } = component

	return (
		<>
			<Description name={name} description={description} />
			<Usage usage={usage} shortName={shortName} />
			<Grid cols="12" rows="3" gapX="6" gapY="6" className="bg-muted-200 mt-4 h-96 rounded-md p-4">
				<div className="bg-muted-600 col-span-12 flex h-full w-full items-center justify-center rounded-sm text-white">Header</div>
				<div className="bg-muted-600 col-span-3 flex h-full w-full items-center justify-center rounded-sm text-white">Sidebar</div>
				<div className="bg-muted-600 col-span-9 flex h-full w-full items-center justify-center rounded-sm text-white">Content</div>
				<div className="bg-muted-600 col-span-12 flex h-full w-full items-center justify-center rounded-sm text-white">Footer</div>
			</Grid>
			<Features features={features} />
			<Parts parts={parts} />

			<H2>Notes</H2>
			<P>
				If you set <Code>inline</Code>, <Code>justify</Code> has no meaning because the <Code>{'<Grid />'}</Code> has the least width possible.
			</P>
			<P>
				Setting <Code>align</Code>, only has meaning if the <Code>{'<Grid />'}</Code> has a height. Otherwise it will have the least height possible.
			</P>
			<P>
				If you set <Code>justify="stretch"</Code>, the children shouldn't have a fixed width. Otherwise they won't stretch.
			</P>
			<P>
				If you set <Code>align="stretch"</Code>, the children shouldn't have a fixed height. Otherwise they won't stretch.
			</P>
			<P>
				Setting <Code>justify="stretch"</Code> has the same effect as setting <Code>w-full</Code> on all the children (assuming <Code>flow="row"</Code>).
			</P>
			<P>
				Setting <Code>align="stretch"</Code> has the same effect as setting <Code>h-full</Code> on all the children (assuming <Code>flow="row"</Code>).
			</P>

			<H2>Accessibility</H2>
			<P>
				Changing visual order creates a disconnect between content and presentation and is, therefore,{' '}
				<Link to="/articles/the-dark-side-of-the-grid" className="underline">
					bad for accessibility
				</Link>
				. For this reason, I've ommitted <Code>grid-auto-flow:row dense</Code> and <Code>grid-auto-flow: column dense</Code>.
			</P>

			<Source fileName={fileName} source={source} />
		</>
	)
}
export default HeadingRoute
